import {
  Card, Heading, HStack, Icon, IconButton, VStack,
} from '@chakra-ui/react';
import { X, Star } from 'lucide-react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { getChunkedUsersByEmail } from '@app/actions/users';
import GroupRow from '@app/components/chakra/group-row';
import { FileInput, FileUploadRoot } from '@app/components/chakra/snippets/file-upload';
import { toaster } from '@app/components/chakra/snippets/toaster';
import UserRow from '@app/components/chakra/user-row';
import { Group } from '@app/types/api/group';
import { User } from '@app/types/api/user';
import { Tooltip } from '@chakra-snippets/tooltip';

const CurrentMembersAndGroups = () => {
  const dispatch = useDispatch();
  const {
    setValue,
    watch,
    trigger,
  } = useFormContext();

  const members = watch('members') || [];
  const leads = watch('leads') || [];
  const subgroups = watch('subgroups') || [];

  const handleRemoveUser = (userId: string | undefined, field: 'members' | 'leads') => {
    if (!userId) return;
    const currentUsers = watch(field) || [];
    setValue(field, currentUsers.filter((user: User) => user.id !== userId));
    trigger(field);
  };

  const handleRemoveGroup = (groupId: string | undefined) => {
    if (!groupId) return;
    const currentGroups = watch('subgroups') || [];
    setValue('subgroups', currentGroups.filter((group: Group) => group.id !== groupId));
    trigger('subgroups');
  };

  const handleFileUpload = async (details: { files: File[]; content?: string }) => {
    const toastId = toaster.create({
      title: 'Uploading users',
      description: 'Please wait while we upload the users',
      type: 'loading',
    });
    const file = details.files[0];
    if (!file) return;

    try {
      const text = details.content || await file.text();
      const emails = text
        .split(/[\n,]/)
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);

      const chunkedUsers = await dispatch(getChunkedUsersByEmail(emails));
      const foundUsers: User[] = (
        chunkedUsers as unknown as { users: User[] }[]
      ).flatMap((i) => i.users || []);
      const foundEmails = foundUsers.map((u) => u.email?.trim().toLowerCase());
      const notFoundEmails = emails.filter((email) => !foundEmails.includes(email));

      if (notFoundEmails.length > 0) {
        toaster.create({
          title: 'Some users were not found:',
          description: notFoundEmails.join(', '),
          type: 'error',
          duration: 10000,
        });
      }

      const currentMembers = watch('members') || [];
      const currentLeads = watch('leads') || [];
      const currentUsers = [...currentMembers, ...currentLeads];
      const newMembers = foundUsers.filter(
        (user) => !currentUsers.some((member: User) => member.id === user._id),
      ).map((user) => ({
        ...user,
        profile: {
          ...user.profile,
        },
        id: user._id,
      }));

      if (newMembers.length > 0) {
        setValue('members', [...currentMembers, ...newMembers]);
        trigger('members');
      }
      if (toastId) {
        toaster.update(toastId, {
          title: 'Users uploaded successfully',
          description: `${newMembers.length} users uploaded`,
          type: 'success',
          duration: 5000,
        });
      }
    } catch (error) {
      toaster.create({
        title: 'Error processing file',
        description: 'Please try again',
        type: 'error',
      });
    }
  };

  return (
    <VStack align="flex-start" h="45vh">
      <HStack justify="space-between" w="100%">
        <Heading h={8} size="md" display="flex" alignItems="center">Current Members</Heading>
        <FileUploadRoot
          w="fit-content"
          onFileAccept={handleFileUpload}
          accept={['.txt', '.csv']}
          inputProps={{
            placeholder: 'Import Existing Users',
          }}
        >
          <Tooltip
            showArrow
            content="Upload a .txt or .csv file with user emails comma or newline separated"
          >
            <FileInput showFileName={false} placeholder="Import Existing Users" size="xs" />
          </Tooltip>
        </FileUploadRoot>
      </HStack>
      <Card.Root
        w="100%"
        h="100%"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <VStack
          align="flex-start"
          py={2}
          overflowY="scroll"
          css={{
            '& .user-row-box:hover': {
              backgroundColor: 'gray.100',
            },
            '& .user-row-box': {
              padding: '8px',
            },
          }}
        >
          {leads.map((lead: User) => (
            <UserRow
              key={lead.id}
              option={{
                ...lead,
                ...lead.profile,
              }}
              showTitle
              size="sm"
              rightElement={(
                <HStack>
                  <Icon color="orange.500"><Star fill="currentColor" /></Icon>
                  <Tooltip showArrow content="Remove lead">
                    <IconButton
                      aria-label="Remove lead"
                      name="Remove lead"
                      variant="ghost"
                      _hover={{
                        bg: 'none',
                      }}
                      onClick={() => lead.id && handleRemoveUser(lead.id, 'leads')}
                    >
                      <Icon><X /></Icon>
                    </IconButton>
                  </Tooltip>
                </HStack>
                  )}
            />
          ))}
          {members.map((member: User) => (
            <UserRow
              key={member.id}
              option={{
                ...member,
                ...member.profile,
              }}
              showTitle
              size="sm"
              rightElement={(
                <Tooltip showArrow content="Remove member">
                  <IconButton
                    aria-label="Remove member"
                    name="Remove member"
                    variant="ghost"
                    _hover={{
                      bg: 'none',
                    }}
                    onClick={() => member.id && handleRemoveUser(member.id, 'members')}
                  >
                    <Icon><X /></Icon>
                  </IconButton>
                </Tooltip>
                  )}
            />
          ))}
          {subgroups.map((group: Group) => (
            <GroupRow
              key={group.id}
              option={group}
              size="sm"
              showMemberCount={false}
              rightElement={(
                <Tooltip showArrow content="Remove group">
                  <IconButton
                    aria-label="Remove group"
                    name="Remove group"
                    variant="ghost"
                    _hover={{
                      bg: 'none',
                    }}
                    onClick={() => group.id && handleRemoveGroup(group.id)}
                  >
                    <Icon><X /></Icon>
                  </IconButton>
                </Tooltip>
                  )}
            />
          ))}
        </VStack>
      </Card.Root>
    </VStack>
  );
};

export default CurrentMembersAndGroups;
